import { Component, inject, OnInit } from '@angular/core';
import { FormControl, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MAT_DIALOG_DATA, MatDialogActions, MatDialogClose, MatDialogContent, MatDialogRef, MatDialogTitle } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { catchError, map, Observable } from 'rxjs';
import { Tag } from 'src/app/entities/tag/tag';
import { Uuid } from 'src/app/entities/value-objects/uuid/uuid';
import { OperationType } from 'src/app/models/operation-types';
import { TagRepository } from 'src/app/services/remote-data-service/repositories/tag-repository/tag.repository';
import { StoreService } from 'src/app/services/store-service/store.service';
import { SnackbarService } from 'src/app/services/snackbar-service/snackbar.service';


/**
 * Component for handling tag creation and updates in a dialog.
 * Uses Angular Material dialog components and reactive forms.
 */
@Component({
  selector: 'app-tags-mutations',
  imports: [
    MatFormFieldModule,
    MatInputModule,
    MatCheckboxModule,
    MatSelectModule,
    MatIconModule,
    MatButtonModule,
    ReactiveFormsModule,
    MatButtonModule,
    MatDialogActions,
    MatDialogClose,
    MatDialogTitle,
    MatDialogContent
  ],
  templateUrl: './tags-mutations.component.html',
  styleUrl: './tags-mutations.component.scss'
})
export class TagsMutationsComponent implements OnInit {
  // Reference to the dialog instance for closing/passing data back
  readonly dialogRef = inject(MatDialogRef<TagsMutationsComponent>);

  // Injected data containing operation type (CREATE/UPDATE) and tag ID.
  data = inject(MAT_DIALOG_DATA).operation as OperationType;
  tagId = inject(MAT_DIALOG_DATA).id as string;

  // Local array to store tags
  tags: Tag[] = [];

  // Observable stream of tags from store
  tags$: Observable<Tag[]>;

  // UI text that changes based on operation type
  title?: string;
  submitButton?: string;

  // Form group for tag name input
  protected form = new FormGroup({
    name: new FormControl() // Control for tag name
  })

  /**
   * Constructor initializes tag repository and store services
   * Sets up subscription to tags store
   */
  constructor(
    protected tagsRep: TagRepository, // Repository for tag CRUD operations
    protected store: StoreService,    // Store service for state management
    private snackbarService: SnackbarService
  ) {
    // Get tags observable from store
    this.tags$ = this.store.tags;

    // Subscribe to tags updates and maintain local copy
    this.tags$.subscribe((tags) => {
      this.tags = tags;
    })
  }

  /**
   * Initialize component based on operation type
   * Sets up form and UI text for either create or update operation
   */
  ngOnInit(): void {
    switch (this.data) {
      case OperationType.CREATE:
        // Setup for tag creation
        this.title = 'Create Tag';
        this.submitButton = 'Create';
        break;
      case OperationType.UPDATE: {
        // Setup for tag update
        this.title = 'Update Tag';
        this.submitButton = 'Update';

        // Find existing tag and populate form
        const currentTag = this.tags.find(t => t.id === this.tagId);

        if (currentTag) {
          this.form.patchValue({
            name: currentTag.label,
          })
        }

        break;
      }
    }
  }

  /**
   * Handle form submission
   * Creates new tag or updates existing one based on operation type
   */
  projectSubmit(): void {
    const form = this.form.value;

    // Create new tag instance for creation
    const newTag = new Tag(
      Uuid.generateId(), // Generate new UUID for new tag
      form.name || ''    // Use form name or empty string
    );

    // Create tag instance for update
    const updateTag = new Tag(
      this.tagId,      // Use existing tag ID
      form.name || ''  // Use form name or empty string
    );

    // Execute appropriate operation
    switch (this.data) {
      case OperationType.CREATE:
        this.addTag(newTag);
        break;
      case OperationType.UPDATE: {
        this.updateTag(updateTag);
        break;
      }
    }
  }


  /**
   * Add a new tag to the repository and update store
   * @param tag The new tag to add
   */
  addTag(tag: Tag): void {
    this.tagsRep.addNewTag(tag).pipe(
      catchError(() => {
        return [] // Return empty array on error
      }),
      map(() => {
        this.store.addEntity(tag);
        this.snackbarService.showSuccess('Tag created');
      })
    ).subscribe()
  }


  /**
   * Update existing tag in repository and store
   * @param tag The tag with updated data
   */
  updateTag(tag: Tag): void {
    this.tagsRep.updateTag(tag).pipe(
      catchError(() => {
        return [] // Return empty array on error
      }),
      map(() => {
        this.store.updateEntity(tag);
        this.snackbarService.showSuccess('Tag updated');
      })
    ).subscribe()
  }
}
